import { useState } from 'react'
import { Botao } from '../../components/Botao'
import { AvisoDeErro, EstadoVazio } from '../../components/Estados'
import { IconeAlerta } from '../../components/Icones'
import { TEXTOS } from '../../lib/glossario'
import { useRegistrarContestacao, useRevogarOcorrencia } from '../../api/queries/iad'
import type { OcorrenciaDoIad } from '../../types/iad'
import { DialogoDeTexto } from '../chamados/fiscalizacao/DialogoDeTexto'
import { dataPorExtenso, instantePorExtenso } from './pontuacao'

/** Que diálogo está aberto e sobre qual ocorrência. */
type Aberto = { acao: 'revogar' | 'contestar'; id: number } | null

/**
 * As ocorrências do mês, as que somam e as que foram retiradas.
 *
 * **As revogadas continuam na lista.** Some da soma, não da vista: uma ocorrência que imputou
 * pontos a uma empresa e depois foi retirada é exatamente o que quem confere o processo precisa
 * enxergar, com o motivo e o autor da retirada ao lado.
 *
 * @param props as ocorrências do mês e se a conta pratica atos sobre elas
 * @returns a lista
 */
export function ListaDeOcorrencias({
  ocorrencias,
  podeAgir,
}: {
  ocorrencias: OcorrenciaDoIad[]
  podeAgir: boolean
}) {
  const t = TEXTOS.iad
  const [aberto, setAberto] = useState<Aberto>(null)
  const revogar = useRevogarOcorrencia()
  const contestar = useRegistrarContestacao()
  const erro = revogar.error ?? contestar.error

  if (ocorrencias.length === 0) {
    return (
      <EstadoVazio
        icone={<IconeAlerta className="h-9 w-9" />}
        titulo={t.semOcorrencias.titulo}
        ajuda={t.semOcorrencias.ajuda}
      />
    )
  }

  return (
    <>
      {/* Retirar da soma e registrar manifestação falhavam em SILÊNCIO: o diálogo fechava, a lista
          continuava idêntica, e não havia o que ler. Dez pontos são um terço do caminho até 7% de
          desconto na nota — quem acha que retirou e não retirou fecha o mês com o número errado.
          É o cenário 51, e a mensagem fica acima da lista, onde o olho já está. */}
      {erro && <AvisoDeErro erro={erro} titulo={TEXTOS.fiscalizacao.acoes.naoDeuCerto} />}

      <ul className="divide-y divide-slate-100 rounded-lg ring-1 ring-slate-200">
        {ocorrencias.map((o) => (
          <Ocorrencia
            key={o.id}
            ocorrencia={o}
            podeAgir={podeAgir}
            aoAgir={(acao) => setAberto({ acao, id: o.id })}
          />
        ))}
      </ul>

      {aberto?.acao === 'revogar' && (
        <DialogoDeTexto
          titulo={t.revogar}
          ajuda="Ela sai da soma e continua visível, com quem retirou, quando e por quê."
          rotulo="Por que está sendo retirada"
          aoConfirmar={(motivo) => {
            revogar.mutate({ id: aberto.id, motivo })
            setAberto(null)
          }}
          aoFechar={() => setAberto(null)}
        />
      )}

      {aberto?.acao === 'contestar' && (
        <DialogoDeTexto
          titulo={t.contestacao}
          ajuda={t.contestacaoAjuda}
          rotulo="O que a contratada respondeu"
          aoConfirmar={(texto) => {
            contestar.mutate({ id: aberto.id, texto })
            setAberto(null)
          }}
          aoFechar={() => setAberto(null)}
        />
      )}
    </>
  )
}

/** Uma ocorrência: o item, o fato, os pontos e o que já foi feito a respeito. */
function Ocorrencia({
  ocorrencia,
  podeAgir,
  aoAgir,
}: {
  ocorrencia: OcorrenciaDoIad
  podeAgir: boolean
  aoAgir: (acao: 'revogar' | 'contestar') => void
}) {
  const t = TEXTOS.iad
  const viva = ocorrencia.revogadaEm == null

  return (
    <li className={viva ? 'space-y-2 px-3 py-3 text-sm' : 'space-y-2 bg-slate-50 px-3 py-3 text-sm'}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <div className="min-w-0">
          <span className="font-medium text-slate-900">Item {ocorrencia.item}</span>{' '}
          <span className="text-slate-500">· {dataPorExtenso(ocorrencia.ocorridoEm)}</span>
          {ocorrencia.gmudCodigo && <span className="text-slate-500"> · {ocorrencia.gmudCodigo}</span>}
          <p className="mt-0.5 text-xs text-slate-600">{ocorrencia.infracao}</p>
        </div>
        <span
          className={
            viva
              ? 'whitespace-nowrap rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold tabular-nums text-red-900'
              : 'whitespace-nowrap rounded-full bg-slate-200 px-2 py-0.5 text-xs font-semibold tabular-nums text-slate-600 line-through'
          }
        >
          {ocorrencia.pontos} pts
        </span>
      </div>

      <p className="text-slate-700">{ocorrencia.descricao}</p>
      <p className="text-xs text-slate-500">
        {ocorrencia.registradaPorNome} · {instantePorExtenso(ocorrencia.registradaEm)}
      </p>

      {ocorrencia.contestacao && (
        <div className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <p className="font-semibold">{t.contestacao}</p>
          <p className="mt-0.5">{ocorrencia.contestacao}</p>
        </div>
      )}

      {!viva && (
        <p className="rounded-lg bg-slate-100 px-3 py-2 text-xs text-slate-700">
          {t.revogada(ocorrencia.revogadaPorNome ?? '')} · {ocorrencia.revogacaoMotivo}
        </p>
      )}

      {viva && podeAgir && (
        <div className="flex flex-wrap gap-2">
          <Botao onClick={() => aoAgir('revogar')}>{t.revogar}</Botao>
          {!ocorrencia.contestacao && <Botao onClick={() => aoAgir('contestar')}>{t.registrarContestacao}</Botao>}
        </div>
      )}
    </li>
  )
}
